import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { X } from "lucide-react";
import mapImage from "@assets/Map_1754471077625.webp";
import { useState, useRef, useEffect } from "react";

interface PropertyMapModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PropertyMapModal({ isOpen, onClose }: PropertyMapModalProps) {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastTouch, setLastTouch] = useState({ x: 0, y: 0 });
  const [initialDistance, setInitialDistance] = useState(0);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const scrollHintTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Reset zoom when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [isOpen]);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      setLastTouch({ x: e.touches[0].clientX, y: e.touches[0].clientY });
      setIsDragging(true);
    } else if (e.touches.length === 2) {
      const distance = Math.sqrt(
        Math.pow(e.touches[0].clientX - e.touches[1].clientX, 2) +
        Math.pow(e.touches[0].clientY - e.touches[1].clientY, 2)
      );
      setInitialDistance(distance);
      setIsDragging(false);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    
    if (e.touches.length === 1 && isDragging && scale > 1) {
      const deltaX = e.touches[0].clientX - lastTouch.x;
      const deltaY = e.touches[0].clientY - lastTouch.y;
      
      setPosition(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      
      setLastTouch({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    } else if (e.touches.length === 2) {
      const distance = Math.sqrt(
        Math.pow(e.touches[0].clientX - e.touches[1].clientX, 2) +
        Math.pow(e.touches[0].clientY - e.touches[1].clientY, 2)
      );
      
      if (initialDistance > 0) {
        const newScale = scale * (distance / initialDistance);
        const clampedScale = Math.min(Math.max(newScale, 1), 4);
        setScale(clampedScale);
        
        // Reset position when zooming out to 1x
        if (clampedScale === 1) {
          setPosition({ x: 0, y: 0 });
        }
      }
      
      setInitialDistance(distance);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setInitialDistance(0);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > 1) {
      setLastTouch({ x: e.clientX, y: e.clientY });
      setIsDragging(true);
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      const deltaX = e.clientX - lastTouch.x;
      const deltaY = e.clientY - lastTouch.y;
      
      setPosition(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      
      setLastTouch({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    // Check if Ctrl (Windows/Linux) or Cmd (Mac) is held
    const isCtrlOrCmd = e.ctrlKey || e.metaKey;
    
    if (isCtrlOrCmd) {
      // Prevent default zoom behavior when using Ctrl/Cmd + scroll
      e.preventDefault();
      
      // Zoom the map
      const newScale = scale + (e.deltaY > 0 ? -0.1 : 0.1);
      const clampedScale = Math.min(Math.max(newScale, 1), 4);
      setScale(clampedScale);
      
      if (clampedScale === 1) {
        setPosition({ x: 0, y: 0 });
      }
    } else {
      // Show hint to user about using Ctrl/Cmd to zoom
      setShowScrollHint(true);
      
      // Clear any existing timeout
      if (scrollHintTimeoutRef.current) {
        clearTimeout(scrollHintTimeoutRef.current);
      }
      
      // Hide the hint after 2 seconds
      scrollHintTimeoutRef.current = setTimeout(() => {
        setShowScrollHint(false);
      }, 2000);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-full max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="px-6 pt-6 pb-2 relative">
          <DialogTitle 
            className="text-center text-2xl sm:text-4xl tracking-wider uppercase"
            style={{ fontFamily: 'Lato', fontWeight: '300' }}
          >
            TOKA RIDGE PROPERTY MAP
          </DialogTitle>
        </DialogHeader>
        
        <div className="px-3 sm:px-6 pb-6">
          <div 
            ref={containerRef}
            className="bg-background rounded-lg shadow-lg overflow-hidden relative cursor-grab active:cursor-grabbing"
            style={{ 
              height: '70vh',
              touchAction: 'none'
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
          >
            <img 
              src={mapImage} 
              alt="Toka Ridge Property Map showing all villas, amenities, and important areas"
              className="w-full h-full object-contain select-none"
              style={{ 
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                transformOrigin: 'center center',
                transition: isDragging ? 'none' : 'transform 0.1s ease-out'
              }}
              draggable={false}
            />
            
            {/* Zoom indicator */}
            {scale > 1 && (
              <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
                {Math.round(scale * 100)}%
              </div>
            )}
            
            {/* Scroll hint overlay */}
            {showScrollHint && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="bg-black bg-opacity-75 text-white px-6 py-4 rounded-lg shadow-lg text-center animate-fade-in">
                  <div className="text-lg font-medium mb-1">
                    Use {navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'} + scroll to zoom
                  </div>
                  <div className="text-sm opacity-90">
                    Use two fingers to move around
                  </div>
                </div>
              </div>
            )}
            
            {/* Instructions */}
            <div className="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white px-3 py-2 rounded text-sm">
              <div className="md:hidden">Pinch to zoom • Drag to pan</div>
              <div className="hidden md:block">
                {navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'}+scroll to zoom • Drag to pan when zoomed
              </div>
            </div>
          </div>
          
          <div className="mt-6 bg-muted rounded-lg p-4 sm:p-6">
            <h3 className="text-lg sm:text-xl mb-4 text-center" style={{ fontFamily: 'Georgia', fontStyle: 'italic' }}>
              Property Layout Guide
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-foreground">
              <div>
                <h4 className="font-semibold text-foreground mb-2">Villa Locations:</h4>
                <ul className="space-y-1 ml-2">
                  <li>• <span className="font-medium">Toka 1 Bed (Atua)</span> - Lakefront villa</li>
                  <li>• <span className="font-medium">Toka 2 Bed (Ngahere)</span> - Forest villa with hot tub</li>
                  <li>• <span className="font-medium">Toka 3 Bed (Ngawha)</span> - Central family villa</li>
                  <li>• <span className="font-medium">Toka 4 Bed (Kōkō)</span> - Premium luxury villa</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold text-foreground mb-2">Key Areas:</h4>
                <ul className="space-y-1 ml-2">
                  <li>• <span className="font-medium">Assembly Point</span> - Emergency meeting location</li>
                  <li>• <span className="font-medium">Beach Access Path</span> - Private lake access</li>
                  <li>• <span className="font-medium">Games Storeroom</span> - Recreation equipment</li>
                  <li>• <span className="font-medium">Beach Cabana</span> - Lakeside relaxation area</li>
                  <li>• <span className="font-medium">Kayaks</span> - Water recreation equipment</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-orange-50 border-l-4 border-orange-400 rounded">
              <p className="text-sm text-orange-800">
                <span className="font-semibold">Important:</span> The red shaded area is the owner's private property. 
                For your safety and privacy, please do not cross the fence into this area. 
                This area contains helicopter landing facilities and large dogs.
              </p>
            </div>
            
            
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}